import { useState } from "react";
import { Loader2, ShieldPlus, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const LICENSE_TYPES = ["RN", "PN", "CNM", "CRNA", "CNS", "CNP"];

const FIELDS = [
  { key: "jurisdictionAbbreviation", label: "State Board (e.g. NJ)", required: true, width: "w-36" },
  { key: "licenseNumber", label: "License Number", required: true, width: "w-44" },
  { key: "licenseType", label: "License Type", required: true, select: LICENSE_TYPES, width: "w-32" },
  { key: "ncsbnId", label: "NCSBN ID (optional)", width: "w-40" },
  { key: "address1", label: "Employment Address", required: true, width: "w-64" },
  { key: "city", label: "City", required: true, width: "w-40" },
  { key: "state", label: "State (e.g. NJ)", required: true, width: "w-28" },
  { key: "zip", label: "ZIP", required: true, width: "w-28" },
  { key: "lastFourSSN", label: "SSN Last 4", required: true, width: "w-32" },
  { key: "birthYear", label: "Birth Year", required: true, width: "w-32" },
];

const nursysError = (detail) => {
  if (detail && detail.errors) {
    const errs = Array.isArray(detail.errors) ? detail.errors : Object.values(detail.errors).flat();
    if (errs.length) return errs.join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  return formatApiErrorDetail(detail);
};

export const NursysEnrollPanel = ({ s, refresh }) => {
  const enrolled = s.nursys?.transaction_id;
  const d = s.data || {};
  const [form, setForm] = useState({
    jurisdictionAbbreviation: d.licenseState || "",
    licenseNumber: d.licenseNumber || "",
    licenseType: { NP: "CNP", RN: "RN" }[d.credentials] || "RN",
    ncsbnId: "",
    address1: d.addressStreet || "",
    city: d.addressCity || "",
    state: d.addressState || "",
    zip: d.addressZip || "",
    lastFourSSN: d.ssnLast4 || "",
    birthYear: d.birthYear || "",
  });
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(!enrolled);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const enroll = async () => {
    setBusy(true); setError(""); setSuccess("");
    try {
      const res = await api.post(`/admin/submissions/${s.id}/nursys-enroll`, form);
      const txn = res.data?.Transaction || {};
      setSuccess(`Enrolled with Nursys — transaction #${txn.TransactionId || "submitted"}. Results usually process within minutes; use Check Status.`);
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(nursysError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const checkStatus = async () => {
    setChecking(true); setError(""); setSuccess("");
    try {
      const res = await api.get(`/admin/submissions/${s.id}/nursys-status`);
      const done = res.data?.ProcessingCompleteFlag;
      setSuccess(done ? "Nursys finished processing this enrollment." : "Nursys is still processing — check again shortly.");
      refresh();
    } catch (err) {
      setError(nursysError(err.response?.data?.detail) || err.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-testid={`nursys-panel-${s.reference_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-[#0B1B2B] flex items-center gap-1.5">
          <ShieldPlus size={13} className="text-[#D4AF37]" /> Nursys e-Notify Enrollment
        </p>
        {enrolled && (
          <span className="text-[10px] text-[#94A3B8]" data-testid={`nursys-meta-${s.reference_id}`}>
            Submitted {new Date(s.nursys.submitted_at).toLocaleString()} by {s.nursys.submitted_by} · Txn #{s.nursys.transaction_id} · {s.nursys.status}
          </span>
        )}
      </div>
      {enrolled && !showForm && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={checkStatus} disabled={checking} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors disabled:opacity-60" data-testid={`nursys-check-status-${s.reference_id}`}>
            {checking ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} className="text-[#D4AF37]" />} Check Status
          </button>
          <button onClick={() => setShowForm(true)} className="text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#64748B] hover:border-[#D4AF37] transition-colors" data-testid={`nursys-reenroll-${s.reference_id}`}>
            Re-enroll / Update
          </button>
        </div>
      )}
      {showForm && (
        <>
          <p className="text-[11px] text-[#94A3B8] mb-3">
            Enrolls this preceptor on the Nursys e-Notify roster for ongoing license and discipline monitoring.
            SSN last-4 and birth year are required by Nursys for record verification only — they are never shown elsewhere.
          </p>
          <div className="flex flex-wrap gap-3">
            {FIELDS.map((f) => (
              <label key={f.key} className={`${f.width} text-[10px] font-semibold uppercase tracking-wide text-[#64748B]`}>
                {f.label}{f.required && <span className="text-red-500"> *</span>}
                {f.select ? (
                  <select value={form[f.key]} onChange={set(f.key)} className="mt-1 w-full text-xs font-normal normal-case tracking-normal px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] bg-white focus:border-[#D4AF37] outline-none" data-testid={`nursys-field-${f.key}-${s.reference_id}`}>
                    {f.select.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input value={form[f.key]} onChange={set(f.key)} className="mt-1 w-full text-xs font-normal normal-case tracking-normal px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] focus:border-[#D4AF37] outline-none" data-testid={`nursys-field-${f.key}-${s.reference_id}`} />
                )}
              </label>
            ))}
          </div>
          <button onClick={enroll} disabled={busy} className="mt-4 flex items-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-lg bg-[#0B1B2B] text-[#D4AF37] hover:bg-[#12263A] transition-colors disabled:opacity-60" data-testid={`nursys-enroll-submit-${s.reference_id}`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldPlus size={13} />} Enroll in Nursys
          </button>
        </>
      )}
      {success && (
        <p className="mt-3 text-xs text-[#166534] flex items-start gap-1.5" data-testid={`nursys-success-${s.reference_id}`}>
          <CheckCircle2 size={13} className="mt-0.5 shrink-0" /> {success}
        </p>
      )}
      {error && (
        <p className="mt-3 text-xs text-red-600 flex items-start gap-1.5" data-testid={`nursys-error-${s.reference_id}`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
};
